import React from 'react';
import { X, Calendar, MapPin, Tag, FileText, Check, AlertCircle } from 'lucide-react';
import type { LeaseApplication } from '../../types/lease-application';
import { formatDate } from '../../utils/dateUtils';
import { formatTextWithLinks } from '../../utils/formatters';

interface LeaseApplicationDetailsModalProps {
  application: LeaseApplication;
  onClose: () => void;
}

export default function LeaseApplicationDetailsModal({
  application,
  onClose,
}: LeaseApplicationDetailsModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Application Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Header Info */}
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-gray-400" />
                <span className="text-lg font-medium text-gray-900">
                  Application #{application.app_id}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="h-4 w-4" />
                <span>Lot: {application.app_lot || 'Not specified'}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-gray-600" />
                <span className="text-gray-600">{application.app_type || 'Not specified'}</span>
              </div>
              <span className="text-sm text-gray-500">
                Sublease ID: {application.app_sublease || 'Not specified'}
              </span>
            </div>
          </div>

          {/* Application Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Application Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">ID:</span>
                <p className="text-sm text-gray-700">{application.app_id}</p>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Lot Number:</span>
                <p className="text-sm text-gray-700">{application.app_lot || 'Not specified'}</p>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Type:</span>
                <p className="text-sm text-gray-700">{application.app_type || 'Not specified'}</p>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Status:</span>
                <p className="text-sm text-gray-700">
                  {application.app_final === 'Y' ? 'Finalized' : 
                   application.app_final === 'rejected' ? 'Rejected' : 
                   application.app_approv_date ? 'Approved' : 'Pending'}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Related Sublease ID:</span>
                <p className="text-sm text-gray-700">{application.app_sublease || 'None'}</p>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Application Date:</span>
                <p className="text-sm text-gray-700">{formatDate(application.app_date)}</p>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Approval Date:</span>
                <p className="text-sm text-gray-700">{application.app_approv_date ? formatDate(application.app_approv_date) : 'Not approved'}</p>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Construction Approval Date:</span>
                <p className="text-sm text-gray-700">{application.app_const_appr_date ? formatDate(application.app_const_appr_date) : 'Not applicable'}</p>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Development Completion Date:</span>
                <p className="text-sm text-gray-700">{application.app_dev_comp ? formatDate(application.app_dev_comp) : 'Not completed'}</p>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Applicant ID:</span>
                <p className="text-sm text-gray-700">{application.app_appl || 'Not specified'}</p>
              </div>
              <div className="space-y-1">
                <span className="text-sm font-medium text-gray-500">Final Status:</span>
                <p className="text-sm text-gray-700">{application.app_final || 'Not specified'}</p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {application.app_note && (
            <div className="space-y-2">
              <h3 className="text-lg font-medium text-gray-900">Notes</h3>
              <div className="text-gray-600 whitespace-pre-wrap">{formatTextWithLinks(application.app_note)}</div>
            </div>
          )}

          {/* Status Timeline */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Application Timeline</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-green-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Submitted</p>
                  <p className="text-sm text-gray-500">{formatDate(application.app_date)}</p>
                </div>
              </div>

              {application.app_approv_date && (
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Approved</p>
                    <p className="text-sm text-gray-500">{formatDate(application.app_approv_date)}</p>
                  </div>
                </div>
              )}

              {application.app_const_appr_date && (
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Construction Approved</p>
                    <p className="text-sm text-gray-500">{formatDate(application.app_const_appr_date)}</p>
                  </div>
                </div>
              )}

              {application.app_dev_comp && (
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Development Completed</p>
                    <p className="text-sm text-gray-500">{formatDate(application.app_dev_comp)}</p>
                  </div>
                </div>
              )}

              {application.app_final === 'Y' && (
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Application Finalized</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {application.app_desc && (
            <div className="space-y-2">
              <h3 className="text-lg font-medium text-gray-900">Description</h3>
              <div className="text-gray-600 whitespace-pre-wrap">{formatTextWithLinks(application.app_desc)}</div>
            </div>
          )}

          {/* Documents */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Documents</h3>
            <div className="space-y-3">
              {application.app_doc && (
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-gray-400 mt-1" />
                  <div>
                    <span className="text-sm font-medium text-gray-600 block">Application Document</span>
                    <div className="text-sm text-gray-600">{formatTextWithLinks(application.app_doc)}</div>
                  </div>
                </div>
              )}
              {application.app_appr_doc && (
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-gray-400 mt-1" />
                  <div>
                    <span className="text-sm font-medium text-gray-600 block">Approval Document</span>
                    <div className="text-sm text-gray-600">{formatTextWithLinks(application.app_appr_doc)}</div>
                  </div>
                </div>
              )}
              {application.app_supp_docs && (
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-gray-400 mt-1" />
                  <div>
                    <span className="text-sm font-medium text-gray-600 block">Supporting Documents</span>
                    <div className="text-sm text-gray-600">{formatTextWithLinks(application.app_supp_docs)}</div>
                  </div>
                </div>
              )}
              {!application.app_doc && !application.app_appr_doc && !application.app_supp_docs && (
                <p className="text-sm text-gray-500">No documents attached</p>
              )}
            </div>
          </div>

          {/* Permit Information */}
          {application.app_permit && (
            <div className="space-y-2">
              <h3 className="text-lg font-medium text-gray-900">Permit Information</h3>
              <div className="text-gray-600">{formatTextWithLinks(application.app_permit)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
